import { supabase, instagramProfiles, visitors, analytics } from '../lib/supabase';
import type { InstagramProfile } from '../lib/supabase';

// Instagram API 응답 타입 정의
interface InstagramApiProfile {
    id: string;
    username: string;
    account_type: 'PERSONAL' | 'BUSINESS';
    media_count: number;
    followers_count?: number;
    follows_count?: number;
    profile_picture_url?: string;
    biography?: string;
    website?: string;
    name?: string;
}

interface InstagramMediaItem {
    id: string;
    media_type: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM';
    media_url: string;
    thumbnail_url?: string;
    caption?: string;
    permalink: string;
    timestamp: string;
    like_count?: number;
    comments_count?: number;
}

// 실제 Instagram API 엔드포인트 (프로덕션에서 사용)
const INSTAGRAM_API_BASE = 'https://graph.instagram.com';
const INSTAGRAM_BASIC_DISPLAY_API = 'https://graph.instagram.com/v18.0';

// 개발용 프록시 서버나 백엔드 엔드포인트
const BACKEND_API_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001/api';

export class InstagramService {
    private accessToken: string | null = null;

    constructor() {
        this.accessToken = import.meta.env.VITE_INSTAGRAM_ACCESS_TOKEN || null;
    }

    /**
     * Instagram 로그인 URL 생성 (OAuth 2.0)
     */
    generateAuthUrl(): string {
        const clientId = import.meta.env.VITE_INSTAGRAM_CLIENT_ID;
        const redirectUri = encodeURIComponent(import.meta.env.VITE_INSTAGRAM_REDIRECT_URI);
        const scope = 'user_profile,user_media';

        return `https://api.instagram.com/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&response_type=code`;
    }

    /**
     * Authorization Code를 Access Token으로 교환
     */
    async exchangeCodeForToken(code: string): Promise<string> {
        try {
            const response = await fetch(`${BACKEND_API_BASE}/instagram/token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    client_id: import.meta.env.VITE_INSTAGRAM_CLIENT_ID,
                    client_secret: import.meta.env.VITE_INSTAGRAM_CLIENT_SECRET,
                    grant_type: 'authorization_code',
                    redirect_uri: import.meta.env.VITE_INSTAGRAM_REDIRECT_URI,
                    code: code
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error_description || 'Failed to exchange code for token');
            }

            this.accessToken = data.access_token;
            return data.access_token;
        } catch (error) {
            console.error('Token exchange failed:', error);
            throw error;
        }
    }

    /**
     * Instagram 프로필 정보 가져오기
     */
    async getProfile(accessToken?: string): Promise<InstagramApiProfile> {
        const token = accessToken || this.accessToken;

        if (!token) {
            throw new Error('Access token is required');
        }

        try {
            // 프로덕션: 실제 Instagram API 호출
            const response = await fetch(
                `${INSTAGRAM_BASIC_DISPLAY_API}/me?fields=id,username,account_type,media_count,profile_picture_url&access_token=${token}`
            );

            if (!response.ok) {
                // 개발 환경: 백엔드 프록시나 모킹 서비스 사용
                return await this.getProfileFromProxy(token);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Failed to fetch Instagram profile:', error);
            // 폴백: 프록시 서버 사용
            return await this.getProfileFromProxy(token);
        }
    }

    /**
     * 프록시 서버를 통한 프로필 정보 가져오기 (개발용)
     */
    private async getProfileFromProxy(accessToken: string): Promise<InstagramApiProfile> {
        try {
            const response = await fetch(`${BACKEND_API_BASE}/instagram/profile`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch profile from proxy');
            }

            return await response.json();
        } catch (error) {
            console.error('Proxy request failed:', error);
            // 최후 수단: 모킹 데이터 반환
            return this.getMockProfile();
        }
    }

    /**
     * 개발용 모킹 데이터
     */
    private getMockProfile(): InstagramApiProfile {
        return {
            id: '17841400001234567',
            username: 'demo_user',
            account_type: 'PERSONAL',
            media_count: 150,
            followers_count: 1250,
            follows_count: 300,
            profile_picture_url: 'https://via.placeholder.com/150x150',
            biography: '? Photography enthusiast | ? Travel lover | ? Living life to the fullest',
            website: 'https://example.com',
            name: 'Demo User'
        };
    }

    /**
     * Instagram 미디어 가져오기
     */
    async getMedia(accessToken?: string, limit: number = 25): Promise<InstagramMediaItem[]> {
        const token = accessToken || this.accessToken;

        if (!token) {
            throw new Error('Access token is required');
        }

        try {
            const response = await fetch(
                `${INSTAGRAM_BASIC_DISPLAY_API}/me/media?fields=id,media_type,media_url,thumbnail_url,caption,permalink,timestamp&limit=${limit}&access_token=${token}`
            );

            if (!response.ok) {
                return this.getMockMedia();
            }

            const data = await response.json();
            return data.data || [];
        } catch (error) {
            console.error('Failed to fetch Instagram media:', error);
            return this.getMockMedia();
        }
    }

    /**
     * 개발용 모킹 미디어 데이터
     */
    private getMockMedia(): InstagramMediaItem[] {
        return Array.from({ length: 12 }, (_, i) => ({
            id: `mock_media_${i + 1}`,
            media_type: i % 3 === 0 ? 'VIDEO' : 'IMAGE',
            media_url: `https://picsum.photos/400/400?random=${i + 1}`,
            thumbnail_url: `https://picsum.photos/150/150?random=${i + 1}`,
            caption: `Mock post #${i + 1} - This is a sample Instagram post for development purposes`,
            permalink: `https://instagram.com/p/mock_${i + 1}`,
            timestamp: new Date(Date.now() - (i * 24 * 60 * 60 * 1000)).toISOString(),
            like_count: Math.floor(Math.random() * 500) + 10,
            comments_count: Math.floor(Math.random() * 50) + 1
        }));
    }

    /**
     * Supabase에 Instagram 프로필 저장
     */
    async saveProfileToDatabase(userId: string, instagramData: InstagramApiProfile, accessToken: string): Promise<InstagramProfile> {
        try {
            const profileData = {
                user_id: userId,
                instagram_id: instagramData.id,
                username: instagramData.username,
                full_name: instagramData.name || instagramData.username,
                biography: instagramData.biography || undefined,
                profile_picture_url: instagramData.profile_picture_url || undefined,
                followers_count: instagramData.followers_count || 0,
                following_count: instagramData.follows_count || 0,
                posts_count: instagramData.media_count || 0,
                is_private: false,
                is_verified: false,
                external_url: instagramData.website || undefined,
                last_synced_at: new Date().toISOString()
            };

            const savedProfile = await instagramProfiles.upsert(profileData);

            // 액세스 토큰을 안전하게 저장 (암호화 권장)
            await this.saveAccessToken(userId, savedProfile.id, accessToken);

            return savedProfile;
        } catch (error) {
            console.error('Failed to save profile to database:', error);
            throw error;
        }
    }

    /**
     * Instagram 계정 연결 해제 - 모든 관련 데이터 완전 정리
     */
    async disconnectInstagram(userId: string): Promise<void> {
        console.log(`🗑️ Instagram 연결 해제 시작: ${userId}`);

        try {
            // 1. saved_instagram_accounts 테이블에서 사용자 계정 찾기
            console.log(`📂 saved_instagram_accounts 테이블 조회 중...`);
            const { data: savedAccounts, error: savedAccountsError } = await supabase
                .from('saved_instagram_accounts')
                .select('id, username')
                .eq('user_id', userId);

            if (savedAccountsError) {
                console.error('❌ saved_instagram_accounts 조회 실패:', savedAccountsError);
                throw savedAccountsError;
            }

            console.log(`📂 찾은 saved_instagram_accounts: ${savedAccounts?.length || 0}개`);

            // 2. instagram_profiles 테이블에서 사용자 프로필 찾기
            console.log(`👤 instagram_profiles 테이블 조회 중...`);
            const { data: instagramProfiles, error: profilesError } = await supabase
                .from('instagram_profiles')
                .select('id, username')
                .eq('user_id', userId);

            if (profilesError) {
                console.error('❌ instagram_profiles 조회 실패:', profilesError);
                // 프로필 조회 실패해도 계속 진행
            }

            console.log(`👤 찾은 instagram_profiles: ${instagramProfiles?.length || 0}개`);

            // 3. 모든 관련 데이터 삭제 시작
            if (savedAccounts && savedAccounts.length > 0) {
                for (const account of savedAccounts) {
                    console.log(`🧹 계정 ${account.username} 관련 데이터 정리 중...`);

                    // 토큰 삭제
                    await this.deleteAccessToken(userId, account.id);

                    // 방문자 추적 세션 정리
                    await this.cleanupVisitorSessions(account.id);
                }

                // saved_instagram_accounts 테이블에서 삭제
                console.log(`🗑️ saved_instagram_accounts 테이블에서 삭제 중...`);
                const { error: deleteError } = await supabase
                    .from('saved_instagram_accounts')
                    .delete()
                    .eq('user_id', userId);

                if (deleteError) {
                    console.error('❌ saved_instagram_accounts 삭제 실패:', deleteError);
                    throw deleteError;
                } else {
                    console.log(`✅ saved_instagram_accounts 삭제 완료: ${savedAccounts.length}개`);
                }
            }

            // 4. instagram_profiles 테이블에서 삭제
            if (instagramProfiles && instagramProfiles.length > 0) {
                console.log(`🗑️ instagram_profiles 테이블에서 삭제 중...`);
                const { error: profileDeleteError } = await supabase
                    .from('instagram_profiles')
                    .delete()
                    .eq('user_id', userId);

                if (profileDeleteError) {
                    console.error('❌ instagram_profiles 삭제 실패:', profileDeleteError);
                    // 프로필 삭제 실패해도 계속 진행
                } else {
                    console.log(`✅ instagram_profiles 삭제 완료: ${instagramProfiles.length}개`);
                }
            }

            // 5. public_instagram_posts 테이블에서 게시물 정리
            console.log(`📸 public_instagram_posts 테이블에서 사용자 게시물 삭제 중...`);
            const { error: postsDeleteError } = await supabase
                .from('public_instagram_posts')
                .delete()
                .eq('user_id', userId);

            if (postsDeleteError) {
                console.error('❌ public_instagram_posts 삭제 실패:', postsDeleteError);
                // 게시물 삭제 실패해도 계속 진행
            } else {
                console.log(`✅ public_instagram_posts 삭제 완료`);
            }

            // 6. instagram_tokens 테이블 정리 (추가 안전장치)
            console.log(`🔑 instagram_tokens 테이블 정리 중...`);
            const { error: tokensDeleteError } = await supabase
                .from('instagram_tokens')
                .delete()
                .eq('user_id', userId);

            if (tokensDeleteError) {
                console.error('❌ instagram_tokens 삭제 실패:', tokensDeleteError);
            } else {
                console.log(`✅ instagram_tokens 삭제 완료`);
            }

            // 7. visitor_analysis_payments 테이블에서 관련 결제 기록 정리
            console.log(`💳 visitor_analysis_payments 테이블 정리 중...`);
            const { error: paymentsDeleteError } = await supabase
                .from('visitor_analysis_payments')
                .delete()
                .eq('user_id', userId);

            if (paymentsDeleteError) {
                console.error('❌ visitor_analysis_payments 삭제 실패:', paymentsDeleteError);
            } else {
                console.log(`✅ visitor_analysis_payments 삭제 완료`);
            }

            // 8. 메모리에서 토큰 정리
            this.accessToken = null;

            console.log(`🎉 Instagram 계정 연결 해제 완료: ${userId}`);
            console.log(`📊 정리된 데이터:`, {
                saved_accounts: savedAccounts?.length || 0,
                instagram_profiles: instagramProfiles?.length || 0,
                posts: '전체 삭제',
                tokens: '전체 삭제',
                payments: '전체 삭제'
            });

        } catch (error) {
            console.error('❌ Instagram 연결 해제 실패:', error);
            throw error;
        }
    }

    /**
     * 저장된 액세스 토큰 삭제
     */
    private async deleteAccessToken(userId: string, profileId: string): Promise<void> {
        try {
            const { error } = await supabase
                .from('instagram_tokens')
                .delete()
                .eq('user_id', userId)
                .eq('profile_id', profileId);

            if (error) {
                console.error('토큰 삭제 실패:', error);
            } else {
                console.log(`✅ 토큰 삭제 완료: ${profileId}`);
            }
        } catch (error) {
            console.error('토큰 삭제 중 오류:', error);
        }
    }

    /**
     * 관련 방문자 추적 세션 정리
     */
    private async cleanupVisitorSessions(profileId: string): Promise<void> {
        try {
            // 방문자 추적 세션 삭제
            const { error: sessionsError } = await supabase
                .from('visitor_tracking_sessions')
                .delete()
                .eq('target_profile_id', profileId);

            if (sessionsError) {
                console.error('방문자 세션 정리 실패:', sessionsError);
            }

            // 방문자 데이터 삭제
            const { error: visitorsError } = await supabase
                .from('profile_visitors')
                .delete()
                .eq('profile_id', profileId);

            if (visitorsError) {
                console.error('방문자 데이터 정리 실패:', visitorsError);
            }

            console.log(`✅ 방문자 데이터 정리 완료: ${profileId}`);
        } catch (error) {
            console.error('방문자 데이터 정리 중 오류:', error);
        }
    }

    /**
     * 액세스 토큰 암호화 저장
     */
    private async saveAccessToken(userId: string, profileId: string, accessToken: string): Promise<void> {
        try {
            // 실제 프로덕션에서는 암호화해서 저장해야 함
            const { error } = await supabase
                .from('instagram_tokens')
                .upsert({
                    user_id: userId,
                    profile_id: profileId,
                    access_token: this.encryptToken(accessToken),
                    created_at: new Date().toISOString(),
                    expires_at: new Date(Date.now() + (60 * 24 * 60 * 60 * 1000)).toISOString() // 60일
                });

            if (error) {
                console.error('Failed to save access token:', error);
            }
        } catch (error) {
            console.error('Token storage error:', error);
        }
    }

    /**
     * 간단한 토큰 암호화 (프로덕션에서는 더 강력한 암호화 사용)
     */
    private encryptToken(token: string): string {
        // 실제로는 crypto 라이브러리나 서버에서 암호화해야 함
        return btoa(token);
    }

    /**
     * 토큰 복호화
     */
    private decryptToken(encryptedToken: string): string {
        return atob(encryptedToken);
    }

    /**
     * 프로필 통계 업데이트
     */
    async updateProfileStats(profileId: string): Promise<void> {
        try {
            const profile = await instagramProfiles.getById(profileId);
            if (!profile) return;

            // 저장된 토큰으로 최신 데이터 가져오기
            const token = await this.getStoredToken(profile.user_id, profileId);
            if (!token) return;

            const latestData = await this.getProfile(token);

            await instagramProfiles.update(profileId, {
                followers_count: latestData.followers_count || profile.followers_count,
                following_count: latestData.follows_count || profile.following_count,
                posts_count: latestData.media_count || profile.posts_count,
                last_synced_at: new Date().toISOString()
            });

        } catch (error) {
            console.error('Failed to update profile stats:', error);
        }
    }

    /**
     * 저장된 토큰 가져오기
     */
    private async getStoredToken(userId: string, profileId: string): Promise<string | null> {
        try {
            const { data, error } = await supabase
                .from('instagram_tokens')
                .select('access_token')
                .eq('user_id', userId)
                .eq('profile_id', profileId)
                .single();

            if (error || !data) return null;

            return this.decryptToken(data.access_token);
        } catch (error) {
            console.error('Failed to get stored token:', error);
            return null;
        }
    }

    /**
     * 방문자 데이터 수집 시뮬레이션
     */
    async collectVisitorData(profileId: string): Promise<void> {
        try {
            // 실제로는 Instagram Insights API나 서드파티 서비스 사용
            const mockVisitorData = this.generateMockVisitorData(profileId);

            for (const visitor of mockVisitorData) {
                await visitors.upsert(visitor);
            }

            // 일별 분석 데이터 업데이트
            await this.updateDailyAnalytics(profileId);
        } catch (error) {
            console.error('Failed to collect visitor data:', error);
        }
    }

    /**
     * 모킹 방문자 데이터 생성
     */
    private generateMockVisitorData(profileId: string) {
        const visitors = [];
        const countries = ['KR', 'US', 'JP', 'UK', 'CA', 'AU', 'DE', 'FR'];
        const devices = ['mobile', 'desktop', 'tablet'];
        const browsers = ['Chrome', 'Safari', 'Firefox', 'Edge'];

        for (let i = 0; i < Math.floor(Math.random() * 10) + 5; i++) {
            const country = countries[Math.floor(Math.random() * countries.length)];
            const device = devices[Math.floor(Math.random() * devices.length)];
            const browser = browsers[Math.floor(Math.random() * browsers.length)];

            visitors.push({
                profile_id: profileId,
                visitor_instagram_id: `visitor_${Date.now()}_${i}`,
                visitor_username: `user${Math.floor(Math.random() * 10000)}`,
                visitor_name: `Visitor ${i + 1}`,
                location: {
                    country: country,
                    city: 'Unknown',
                    region: 'Unknown'
                },
                device_info: {
                    type: device,
                    browser: browser,
                    os: device === 'mobile' ? 'iOS' : 'Windows'
                },
                user_agent: `Mozilla/5.0 (${device})`,
                visit_duration: Math.floor(Math.random() * 300) + 30,
                pages_viewed: Math.floor(Math.random() * 5) + 1,
                referrer: Math.random() > 0.5 ? 'instagram.com' : 'direct',
                session_id: `session_${Date.now()}_${i}`,
                is_returning_visitor: Math.random() > 0.7
            });
        }

        return visitors;
    }

    /**
     * 일별 분석 데이터 업데이트
     */
    private async updateDailyAnalytics(profileId: string): Promise<void> {
        try {
            const today = new Date().toISOString().split('T')[0];

            // Supabase 함수 호출
            const { error } = await supabase.rpc('aggregate_daily_analytics', {
                profile_uuid: profileId,
                target_date: today
            });

            if (error) {
                console.error('Failed to update daily analytics:', error);
            }
        } catch (error) {
            console.error('Analytics update error:', error);
        }
    }
}

// 싱글톤 인스턴스 내보내기
export const instagramService = new InstagramService(); 